import type { AttentionText } from "@care-by-ella/contracts";

// TES-1001 — the doctor's two headings above the risk material, and the
// small pieces of "Information att uppmärksamma" that are worth pinning down.
//
// Pure on purpose: no React, no runtime imports (the unit tests in
// `apps/admin/test` run it with plain `node --test`).

/**
 * Ö-250, Emelie's example verbatim: "Akutlarm: 1 – agera nu". The count is
 * the open acute alarms, which are never also counted in "Riskflaggor: X".
 */
export function acuteAlarmHeading(count: number): string {
  return `Akutlarm: ${count} – agera nu`;
}

/** Ö-249 — the heading of the free-text list, with its count. */
export function attentionHeading(count: number): string {
  return `Information att uppmärksamma: ${count}`;
}

/**
 * The DOM id of one answer inside its source view: the target of the link
 * to the source answer ("länk till källsvaret").
 */
export function attentionAnswerAnchor(
  submissionId: string,
  fieldKey: string,
): string {
  return `svar-${submissionId}-${fieldKey}`;
}

/**
 * Submission date AND time — Ö-249 asks for both ("inskickningsdatum och
 * tid"). Always Swedish time, whatever the doctor's machine is set to.
 */
export function submittedAtSv(iso: string): string {
  const d = new Date(iso);
  const date = d.toLocaleDateString("sv-SE", { timeZone: "Europe/Stockholm" });
  const time = d.toLocaleTimeString("sv-SE", {
    timeZone: "Europe/Stockholm",
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${date} ${time}`;
}

/**
 * The answer that opened this follow-up, as one line: "question: answer".
 * Null for a question that is always shown.
 */
export function parentAnswerLine(item: AttentionText): string | null {
  if (!item.parent) return null;
  const question = item.parent.label ?? item.parent.fieldKey;
  return `${question}: ${item.parent.value}`;
}

/**
 * The open acute alarm count and the risk flag count the header shows.
 * `acuteAlarmCount` / `formFlagCount` come from the server (split with the
 * same splitRiskFlagRows as the list). An older server sent one combined
 * `formFlagCount` that already included the alarms, and no
 * `acuteAlarmCount`: the alarms are then taken out of it, so the same signal
 * is still not counted twice.
 */
export function headerRiskCounts(input: {
  acuteAlarmCount?: number;
  formFlagCount?: number;
  openAlarms: number;
  fallbackFlagSum: number;
}): { acute: number; flags: number } {
  if (input.acuteAlarmCount !== undefined) {
    return {
      acute: input.acuteAlarmCount,
      flags: input.formFlagCount ?? input.fallbackFlagSum,
    };
  }
  if (input.formFlagCount !== undefined) {
    return {
      acute: input.openAlarms,
      flags: Math.max(0, input.formFlagCount - input.openAlarms),
    };
  }
  return { acute: input.openAlarms, flags: input.fallbackFlagSum };
}
